import { apiSlice } from '../../../store/api/apiSlice';
import type {
  AddClosingPackageItemBody,
  ClosingPackageCommentBody,
  ClosingPackageCompositionItemDto,
  ClosingPackageCompositionResult,
  ClosingPackageConfirmFormationResult,
  ClosingPackageInitializeCompositionResult,
  ClosingPackageJournalResult,
  ClosingPackageProjection,
  ConfirmClientReceiptBody,
  RegisterClientApprovalBody,
  RegisterEdoDispatchBody,
  ConfirmEdoReceiptBody,
  RegisterOriginalsDispatchBody,
  RegisterOriginalsPreparedBody,
  RegisterScanDispatchBody,
  ReplaceClosingPackageItemBody,
} from '../types';

type ApiEnvelope<T> = {
  success?: boolean;
  data?: T;
  error?: string;
  code?: string;
};

function unwrapData<T>(response: ApiEnvelope<T>, errorMessage: string): T {
  if (response?.data === undefined || response?.data === null) {
    throw new Error(response?.error || errorMessage);
  }
  return response.data;
}

function closingPackageTags(applicationId: number) {
  return [
    { type: 'ClosingPackage' as const, id: applicationId },
    { type: 'Document' as const },
    { type: 'Application' as const, id: applicationId },
    'Workspace' as const,
    'ApplicationControlTasks' as const,
  ];
}

function compositionPath(applicationId: number, suffix = ''): string {
  return `/applications/${applicationId}/closing-package/composition${suffix}`;
}

/**
 * Closing Package RTK layer — composition + workflow endpoints.
 * Projection remains the primary read model; composition GET is optional detail.
 */
export const closingPackageApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getClosingPackageProjection: builder.query<ClosingPackageProjection, number>({
      query: (applicationId) =>
        `/documents-read/list?view=closing-package&applicationId=${applicationId}`,
      transformResponse: (response: ApiEnvelope<ClosingPackageProjection>) =>
        unwrapData(response, 'Closing package projection is missing'),
      providesTags: (_result, _error, applicationId) => [
        { type: 'ClosingPackage', id: applicationId },
      ],
    }),

    getClosingPackageComposition: builder.query<ClosingPackageCompositionResult, number>({
      query: (applicationId) => compositionPath(applicationId),
      transformResponse: (response: ApiEnvelope<ClosingPackageCompositionResult>) =>
        unwrapData(response, 'Closing package composition is missing'),
      providesTags: (_result, _error, applicationId) => [
        { type: 'ClosingPackage', id: applicationId },
      ],
    }),

    getClosingPackageJournal: builder.query<ClosingPackageJournalResult, number>({
      query: (applicationId) => compositionPath(applicationId, '/journal'),
      transformResponse: (response: ApiEnvelope<ClosingPackageJournalResult>) =>
        unwrapData(response, 'Closing package journal is missing'),
      providesTags: (_result, _error, applicationId) => [
        { type: 'ClosingPackage', id: applicationId },
      ],
    }),

    addClosingPackageItem: builder.mutation<
      ClosingPackageCompositionItemDto,
      { applicationId: number; body: AddClosingPackageItemBody }
    >({
      query: ({ applicationId, body }) => ({
        url: compositionPath(applicationId, '/items'),
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<ClosingPackageCompositionItemDto>) =>
        unwrapData(response, 'Failed to add composition item'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    replaceClosingPackageItem: builder.mutation<
      ClosingPackageCompositionItemDto,
      { applicationId: number; itemId: number; body: ReplaceClosingPackageItemBody }
    >({
      query: ({ applicationId, itemId, body }) => ({
        url: compositionPath(applicationId, `/items/${itemId}/replace`),
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<ClosingPackageCompositionItemDto>) =>
        unwrapData(response, 'Failed to replace composition item'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    excludeClosingPackageItem: builder.mutation<
      ClosingPackageCompositionItemDto,
      { applicationId: number; itemId: number; body?: ClosingPackageCommentBody }
    >({
      query: ({ applicationId, itemId, body }) => ({
        url: compositionPath(applicationId, `/items/${itemId}/exclude`),
        method: 'POST',
        body: body ?? {},
      }),
      transformResponse: (response: ApiEnvelope<ClosingPackageCompositionItemDto>) =>
        unwrapData(response, 'Failed to exclude composition item'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    restoreClosingPackageItem: builder.mutation<
      ClosingPackageCompositionItemDto,
      { applicationId: number; itemId: number; body?: ClosingPackageCommentBody }
    >({
      query: ({ applicationId, itemId, body }) => ({
        url: compositionPath(applicationId, `/items/${itemId}/restore`),
        method: 'POST',
        body: body ?? {},
      }),
      transformResponse: (response: ApiEnvelope<ClosingPackageCompositionItemDto>) =>
        unwrapData(response, 'Failed to restore composition item'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    initializeClosingPackageComposition: builder.mutation<
      ClosingPackageInitializeCompositionResult,
      { applicationId: number; body?: ClosingPackageCommentBody }
    >({
      query: ({ applicationId, body }) => ({
        url: compositionPath(applicationId, '/initialize'),
        method: 'POST',
        body: body ?? {},
      }),
      transformResponse: (response: ApiEnvelope<ClosingPackageInitializeCompositionResult>) =>
        unwrapData(response, 'Failed to initialize closing package composition'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    confirmClosingPackageFormation: builder.mutation<
      ClosingPackageConfirmFormationResult,
      { applicationId: number; body?: ClosingPackageCommentBody }
    >({
      query: ({ applicationId, body }) => ({
        url: compositionPath(applicationId, '/confirm'),
        method: 'POST',
        body: body ?? {},
      }),
      transformResponse: (response: ApiEnvelope<ClosingPackageConfirmFormationResult>) =>
        unwrapData(response, 'Failed to confirm closing package formation'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    registerClosingPackageScanDispatch: builder.mutation<
      unknown,
      { applicationId: number; body: RegisterScanDispatchBody }
    >({
      query: ({ applicationId, body }) => ({
        url: `/applications/${applicationId}/closing-package/dispatch/scans`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        unwrapData(response, 'Failed to register scan dispatch'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    registerClosingPackageApproval: builder.mutation<
      unknown,
      { applicationId: number; body: RegisterClientApprovalBody }
    >({
      query: ({ applicationId, body }) => ({
        url: `/applications/${applicationId}/closing-package/approval`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        unwrapData(response, 'Failed to register client approval'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    registerOriginalsPrepared: builder.mutation<
      unknown,
      { applicationId: number; body?: RegisterOriginalsPreparedBody }
    >({
      query: ({ applicationId, body }) => ({
        url: `/applications/${applicationId}/closing-package/originals/prepare`,
        method: 'POST',
        body: body ?? {},
      }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        unwrapData(response, 'Failed to register originals preparation'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    registerOriginalsDispatch: builder.mutation<
      unknown,
      { applicationId: number; body: RegisterOriginalsDispatchBody }
    >({
      query: ({ applicationId, body }) => ({
        url: `/applications/${applicationId}/closing-package/originals/dispatch`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        unwrapData(response, 'Failed to register originals dispatch'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    confirmOriginalsReceipt: builder.mutation<
      unknown,
      { applicationId: number; body: ConfirmClientReceiptBody }
    >({
      query: ({ applicationId, body }) => ({
        url: `/applications/${applicationId}/closing-package/originals/receipt`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        unwrapData(response, 'Failed to confirm originals receipt'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    startEdoDispatch: builder.mutation<unknown, { applicationId: number }>({
      query: ({ applicationId }) => ({
        url: `/applications/${applicationId}/closing-package/originals/edo/start`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        unwrapData(response, 'Failed to start EDO originals delivery'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    registerEdoDispatch: builder.mutation<
      unknown,
      { applicationId: number; body: RegisterEdoDispatchBody }
    >({
      query: ({ applicationId, body }) => ({
        url: `/applications/${applicationId}/closing-package/originals/edo/dispatch`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        unwrapData(response, 'Failed to register EDO dispatch'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),

    confirmEdoReceipt: builder.mutation<
      unknown,
      { applicationId: number; body: ConfirmEdoReceiptBody }
    >({
      query: ({ applicationId, body }) => ({
        url: `/applications/${applicationId}/closing-package/originals/edo/receipt`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<unknown>) =>
        unwrapData(response, 'Failed to confirm EDO receipt'),
      invalidatesTags: (_r, _e, { applicationId }) => closingPackageTags(applicationId),
    }),
  }),
});

export const {
  useGetClosingPackageProjectionQuery,
  useLazyGetClosingPackageProjectionQuery,
  useGetClosingPackageCompositionQuery,
  useLazyGetClosingPackageCompositionQuery,
  useGetClosingPackageJournalQuery,
  useAddClosingPackageItemMutation,
  useReplaceClosingPackageItemMutation,
  useExcludeClosingPackageItemMutation,
  useRestoreClosingPackageItemMutation,
  useInitializeClosingPackageCompositionMutation,
  useConfirmClosingPackageFormationMutation,
  useRegisterClosingPackageScanDispatchMutation,
  useRegisterClosingPackageApprovalMutation,
  useRegisterOriginalsPreparedMutation,
  useRegisterOriginalsDispatchMutation,
  useConfirmOriginalsReceiptMutation,
  useStartEdoDispatchMutation,
  useRegisterEdoDispatchMutation,
  useConfirmEdoReceiptMutation,
} = closingPackageApi;
